/**
 * German. Translated from the Dutch catalogue (`nl.ts`), which has the keys and the comments that explain them.
 *
 * - The user is addressed informally ("du"), like the Dutch "je".
 */
import type {Catalogue} from './nl';

export const de: Catalogue = {
    'common.close': 'Schließen',
    'common.cancel': 'Abbrechen',
    'common.error': 'Fehler',
    'common.retry': 'Erneut versuchen',
    'common.loading': 'Wird geladen...',
    'common.unknownError': 'Unbekannter Fehler',
    'common.busy': 'Bitte warten… das Ergebnis kommt.',
    'common.optional': '(optional)',

    'errorPage.heading': 'Diese Seite konnte nicht angezeigt werden',
    'errorPage.message':
        'Im Terminal ist ein Fehler aufgetreten, er wurde an die Entwickler weitergeleitet. Versuche es erneut, oder gehe zur Startseite, wenn es wieder passiert.',
    'errorPage.loadFailed': 'Die Daten konnten nicht geladen werden',
    'errorPage.back': 'Zurück',
    'errorPage.home': 'Startseite',
    'notFound.heading': 'Diese Seite gibt es nicht',
    'notFound.message': 'Prüfe die Adresse, oder gehe zur Startseite, um weiterzuarbeiten.',

    'topNav.changeStation': 'Station wechseln',
    'topNav.logout': 'Abmelden',
    'topNav.userMenu': 'Benutzermenü von {name}',
    'topNav.language': 'Sprache',
    'topNav.languageButton': 'Sprache: {language}',
    'topNav.accountLanguage': 'Aus meinem Konto ({language})',
    'topNav.menuArrowUp': 'Pfeil nach oben',
    'topNav.menuArrowDown': 'Pfeil nach unten',
    'topNav.menuPick': 'wählen',
    'topNav.menuConfirm': 'bestätigen',
    'topNav.menuClose': 'schließen',
    'topNav.lightTheme': 'Helles Design',
    'topNav.darkTheme': 'Dunkles Design',
    'topNav.environment': 'Umgebung {environment}, zu {other} wechseln',

    'update.available': 'Neue Version verfügbar',
    'update.reloadNow': 'Jetzt neu laden',
    'update.reloadWhenIdle': 'wird neu geladen, sobald das Terminal frei ist',
    'update.reloadIn': 'wird automatisch neu geladen in {time}',
    'update.seconds': {one: '{count} s', other: '{count} s'},
    'update.minutes': {one: '{count} min', other: '{count} min'},

    'environment.title': 'Achtung',
    'environment.switch': 'Zu development wechseln',
    'environment.cancel': 'Abbrechen',
    'environment.warning':
        'Das Shipping Terminal wird mit der Testumgebung verbunden und zeigt keine Produktionsdaten. Diese Umgebung ist nur zum Testen neuer Funktionen gedacht.',

    'login.title': 'Wer meldet sich an?',
    'login.loadingUsers': 'Benutzer werden geladen...',
    'pin.verifying': 'PIN wird geprüft...',
    'pin.prompt': 'Gib deine PIN ein',
    'pin.promptIdle': 'Inaktiv: Gib deine PIN erneut ein',
    'pin.digit': 'Ziffer {index} von {total}',
    'pin.changeUser': 'Benutzer wechseln',
    'pin.errorInvalid': 'Falsche PIN oder zu viele Versuche. Bitte versuche es erneut.',
    'pin.errorFailed': 'Beim Prüfen der PIN ist ein Fehler aufgetreten: {message}',

    'terminal.loadingPrinters': 'Verfügbare Drucker werden geladen...',
    'terminal.noPrinters': 'Keine Drucker gefunden',
    'terminal.selectStation': 'Station auswählen',
    'terminal.selectStationHint': 'Klicke auf den Drucker dieser Station oder drücke seine Ziffer',
    'terminal.status.idle': 'Bereit zum Scannen',
    'terminal.status.notLive': 'Status nicht live',
    'terminal.status.mayBeOutdated': 'Status möglicherweise veraltet',

    'terminal.actions': 'Aktionen',
    'terminal.button.printLabel': 'Zusätzliches Etikett drucken',
    'terminal.button.manco': 'Klärfall',
    'terminal.button.deleteShipment': 'Sendungen löschen',
    'terminal.button.openInAdmin': 'Im Admin öffnen',

    'order.number': 'Bestellung',
    'order.numberPlaceholder': 'Bestellnummer',
    'order.scan': 'Scannen',
    'order.pickedBy': 'Kommissioniert von {user}',
    'order.pickedByUnknown': 'unbekannt',
    'order.created': 'Erstellt {date}',
    'order.scanPrompt': 'Bestellung scannen',
    'order.scanHint': 'Oder gib die Nummer ein und drücke Enter',
    'scan.notRecognized': 'Scan nicht erkannt',
    'scan.notAnOrder':
        '„{code}“ ist keine Auftragsnummer und kein Containercode. Scanne erneut oder gib die Auftragsnummer ein (F2).',
    'scan.tooSlow':
        '„{code}“ kam für einen Scan zu langsam an. Scanne erneut oder gib die Auftragsnummer im Eingabefeld ein (F2).',
    'scan.invalidInput':
        'Gib eine Auftragsnummer mit mindestens 6 Ziffern ein oder einen Containercode, der mit B beginnt.',
    'scan.failed': '„{code}“ konnte nicht geladen werden',
    'scan.noOrderInContainer': 'In diesem Behälter ist keine offene Bestellung',
    'order.products': 'Verpacken',
    'order.shipping': 'Versand',
    'order.noShipping': 'Keine Versandart',
    'order.warehouseNote': 'Lagernotiz',
    'order.noWarehouseNote': 'Keine Lagernotiz',
    'order.warehouseNoteRead': 'Gelesen',
    'order.warnings': 'Achtung beim Verpacken',
    'order.warning.hazard': 'Warnetiketten aufkleben',
    'order.warning.cans': 'Diese Bestellung enthält Dosen: beim Verpacken beachten',
    'order.warning.cargo': 'Versand als Fracht: beim Drucken die Palettengröße wählen',
    'order.hazardImageAlt': 'Warnetikett',

    'meta.items': 'Artikel',
    'meta.weight': 'Gewicht',
    'meta.largestSize': 'Größte Maße (L × B × H)',
    'meta.centimeters': '{value} cm',
    'meta.notApplicable': 'k. A.',

    'address.title': 'Lieferadresse',

    'addressError.title': 'Adresse korrigieren',
    'addressError.hint': 'Korrigiere die Adresse und erstelle das Etikett erneut.',
    'addressError.createLabel': 'Etikett erstellen',
    'addressError.searchGoogle': 'Adresse bei Google suchen',
    'addressError.firstName': 'Vorname',
    'addressError.lastName': 'Nachname',
    'addressError.company': 'Firma',
    'addressError.street': 'Straße',
    'addressError.houseNumber': 'Hausnummer',
    'addressError.addition': 'Zusatz',
    'addressError.zipCode': 'PLZ',
    'addressError.city': 'Ort',
    'addressError.email': 'E-Mail',
    'addressError.phone': 'Telefonnummer',
    'addressError.country': 'Land',

    'country.NL': 'Niederlande',
    'country.BE': 'Belgien',
    'country.FR': 'Frankreich',
    'country.IT': 'Italien',
    'country.DE': 'Deutschland',

    'manco.title': 'Klärfall melden',
    'manco.address': 'Adressdaten sind falsch',
    'manco.customs': 'Fehler bei Auslandssendung',
    'manco.other': 'Sonstiges',
    'manco.reported': 'Klärfall gemeldet',
    'manco.failed': 'Klärfall konnte nicht gemeldet werden: {message}',

    'packing.title': 'Achtung!',
    'packing.cans': 'Dosen sorgfältig verpacken',

    'cargo.title': 'Fracht',
    'cargo.pallet': 'Palette',
    'cargo.halfPallet': 'Halbpalette',
    'cargo.miniPallet': 'Minipalette',
    'cargo.blockPallet': 'Blockpalette',
    'cargo.packages': 'Stückgut',

    'printLabel.title': 'Zusätzliche Etiketten drucken',
    'printLabel.titleAuto': 'Etiketten drucken',
    'printLabel.estimated': 'Geschätzt benötigte Etiketten: {count}',
    'printLabel.otherAmount': 'Andere Anzahl:',
    'printLabel.count': {one: '{count} Etikett', other: '{count} Etiketten'},
    'printLabel.print': 'Drucken',

    'warningLabels.title': 'Achtung!',
    'warningLabels.message': 'Diese Bestellung hat Warnetiketten!',
    'warningLabels.imageAlt': 'Warnetikett',

    'removeShipments.title': 'Sendungen löschen?',
    'removeShipments.text':
        'Alle Sendungen der Bestellung {id} werden gelöscht und beim Versanddienstleister storniert. Der Bestand wird zurückgebucht und die Bestellung steht danach wieder auf „bezahlt“.',

    'shipping.printWarning': 'Achtung',
    'shipping.shipmentRemoved': 'Sendungen gelöscht',
    'shipping.warningMancoReported':
        'Diese Bestellung wurde als Manco gemeldet und ist möglicherweise nicht vollständig kommissioniert',
    'shipping.orderFailed': 'Bestellung {id} wurde nicht versandt',
    'shipping.stillPrinting': 'Bestellung {id} wird noch gedruckt: scanne erneut, sobald das erledigt ist',

    'removeShipments.failed': 'Die Sendungen von Auftrag {id} wurden nicht gelöscht: {message}',
    'removeShipments.removeAnyway': 'Trotzdem löschen',

    'outcome.unknownPrint': 'Unbekannt, ob die Etiketten von Auftrag {id} gedruckt wurden',
    'outcome.unknownPrintHint':
        'Vom Server kam keine eindeutige Antwort. Prüfe den Drucker und den Auftrag im Admin (F11), bevor du erneut druckst.',
    'outcome.unknownRemove': 'Unbekannt, ob die Sendungen von Auftrag {id} gelöscht wurden',
    'outcome.unknownRemoveHint':
        'Vom Server kam keine eindeutige Antwort. Prüfe den Auftrag im Admin (F11), bevor du es erneut versuchst.',
    'outcome.unknownManco': 'Unbekannt, ob Auftrag {id} gemeldet wurde',
    'outcome.unknownMancoHint':
        'Vom Server kam keine eindeutige Antwort. Prüfe den Auftrag im Admin (F11), bevor du ihn erneut meldest.',
    'outcome.busy': 'Für Auftrag {id} läuft noch eine andere Versandaktion',
    'outcome.busyHint':
        'Warte, bis sie abgeschlossen ist, und prüfe den Auftrag dann im Admin (F11), bevor du es erneut versuchst.',
    'outcome.retryAfter': {
        one: 'Versuche es in {count} Sekunde erneut.',
        other: 'Versuche es in {count} Sekunden erneut.',
    },
    'outcome.reason': 'Meldung des Servers: {message}',
    'outcome.printFailed': 'Die Etiketten von Auftrag {id} wurden erstellt, aber nicht gedruckt',
    'outcome.printFailedHint':
        'Nicht erneut drucken, sonst werden sie doppelt erstellt. Lösche zuerst die Sendungen (F10) und drucke dann erneut.',
    'outcome.printFailedExtraHint':
        'Nicht erneut drucken, sonst werden sie doppelt erstellt. Prüfe den Auftrag im Admin (F11).',

    'apiError.notFound': 'Nicht gefunden',
    'apiError.sessionExpired': 'Die Sitzung ist abgelaufen. Melde dich erneut an.',
    'apiError.printerNotFound': 'Der Drucker wurde nicht gefunden. Wähle die Packstation erneut.',
    'apiError.personalAccountRequired': 'Das geht nur mit einem persönlichen Konto, nicht mit einem gemeinsamen',
    'apiError.validationFailed': 'Ungültige Angaben: {message}',
    'apiError.internationalShipment': 'Internationale Sendung: diese verschickt der Kundenservice',
    'apiError.shipsWithParentOrder': 'Dies ist eine Nachlieferung: sie geht mit der Hauptbestellung mit',
    'apiError.tooEarlyForCarrier': 'Noch zu früh für den Versanddienstleister (PostNL ab 7:30)',
    'apiError.orderAlreadySent': 'Die Bestellung wurde bereits versandt',
    'apiError.orderNotPaid': 'Die Bestellung ist nicht bezahlt',
    'apiError.vatNumberNotValidated': 'Die USt-IdNr. des Kunden wurde noch nicht geprüft',
    'apiError.shippingDateInFuture': 'Das Versanddatum der Bestellung liegt in der Zukunft',
    'apiError.wrongPackingStation': 'Diese Bestellung kann nicht von dieser Packstation versandt werden',
    'apiError.paymentNotFound': 'Die Zahlung wurde beim Zahlungsanbieter nicht gefunden',
    'apiError.paymentAuthorizationLost': 'Die Autorisierung der Zahlung ist abgelaufen',
    'apiError.customerInvoiceMissing': 'Die Rechnung des Kunden fehlt',
    'apiError.cargoShippingMethod': 'Dies ist eine Frachtsendung: wähle eine Palettengröße',
    'apiError.notCargoShippingMethod': 'Dies ist keine Frachtsendung',
    'apiError.orderHasShipments': 'Die Bestellung hat bereits Sendungen, deren Etiketten nicht gedruckt wurden',
    'apiError.orderHasNoShipments': 'Die Bestellung hat keine Sendungen',
    'apiError.recipientNameInvalid': 'Der Versanddienstleister lehnt den Namen ab',
    'apiError.postalCodeInvalid': 'Der Versanddienstleister lehnt die Postleitzahl ab',
    'apiError.houseNumberInvalid': 'Der Versanddienstleister lehnt die Hausnummer oder den Zusatz ab',
    'apiError.phoneInvalid': 'Der Versanddienstleister lehnt die Telefonnummer ab',
    'apiError.emailInvalid': 'Der Versanddienstleister lehnt die E-Mail-Adresse ab',
    'apiError.addressInvalid': 'Der Versanddienstleister lehnt die Straße oder den Ort ab',
    'apiError.servicePointInvalid': 'Der Abholpunkt ist nicht gültig',
    'apiError.labelRejected': 'Der Versanddienstleister lehnt das Etikett ab: {message}',
    'apiError.adrConfigurationMissing': 'Die ADR-Angaben eines Produkts fehlen',
    'apiError.shipmentFailed': 'Die Sendung ist fehlgeschlagen: {message}',
    'apiError.labelsNotPrinted': 'Die Etiketten wurden erstellt, aber nicht gedruckt',
    'apiError.orderBusy': 'Für diese Bestellung läuft noch eine andere Aktion',
    'apiError.paymentProviderUnavailable': 'Der Zahlungsanbieter ist nicht erreichbar. Versuche es gleich noch einmal.',
};
